<template>
  <div class="mob-bottom-nav">
    <button
      v-for="item in filteredNavItems"
      :key="item.id"
      class="mob-bottom-nav__item"
      :class="{ active: isActiveItem(item) }"
      @click="navigateToItem(item)"
    >
      <div class="mob-bottom-nav__icon">
        <SvgIcon :icon="item.icon" width="24" height="24" />
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useCampaignStore } from '@/store/campaign';
import { useUIStore } from '@/store/ui';
import { USER_ROLES, UserRoleLevels } from '@/declare/enums/user.enum';
import SvgIcon from '@/components/UI/SvgIcon.vue';

// Stores
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const campaignStore = useCampaignStore();
const uiStore = useUIStore();

// Computed properties
const currentBusiness = computed(() => authStore.currentBusiness);
const currentCampaign = computed(() => campaignStore.currentCampaign);
const hasCampaignPermission = computed(() => {
  if (currentCampaign.value) {
    const userRoleLevel = UserRoleLevels[currentCampaign.value.role!];
    const minimumRoleLevel = UserRoleLevels[USER_ROLES.Reader];
    return userRoleLevel <= minimumRoleLevel;
  }
  return true;
});

const leadView = computed(() => {
  if (uiStore.leadView === 'cards') {
    return 'cards';
  } else {
    return 'leads';
  }
});

const leadPath = computed(() => {
  const base = `/b/${currentBusiness.value?._id}`;

  if (currentCampaign.value) {
    const campaignBase = `${base}/c/${currentCampaign.value?._id}/leads`;
    return leadView.value === 'cards' ? `${campaignBase}/cards` : campaignBase;
  } else {
    return leadView.value === 'cards' ? `${base}/leads/cards/all` : `${base}/leads/all`;
  }
});

// Mobile navigation items based on MainBar menu (reversed order)
const navItems = computed(() => [
  {
    id: 'settings',
    icon: 'ic-settings',
    link: `/b/${currentBusiness.value?._id}/settings/profile`,
    permission: true
  },
  {
    id: 'users',
    icon: 'ic-people',
    link: `/b/${currentBusiness.value?._id}/users`,
    permission: computed(() => authStore.checkPermission(USER_ROLES.Owner))
  },
  {
    id: 'leads',
    icon: 'ic-mob-person',
    link: leadPath.value,
    permission: hasCampaignPermission
  },
  {
    id: 'campaigns',
    icon: 'ic-star',
    link: `/b/${currentBusiness.value?._id}/campaigns`,
    permission: hasCampaignPermission
  },
  {
    id: 'dashboard',
    icon: 'ic-home',
    link: '/dashboard',
    permission: true
  }
]);

// Filter items based on permissions
const filteredNavItems = computed(() => {
  return navItems.value.filter(item =>
    typeof item.permission === 'boolean' ? item.permission : item.permission.value
  );
});

// Check if item is active
const isActiveItem = (item: any) => {
  return route.path === item.link;
};

// Navigation method
function navigateToItem(item: any) {
  router.push(item.link);
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/mobile/mobile-rtl' as *;

.mob-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #E5E5E5;
  border-radius: 16px 16px 0 0;
  padding: 10px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  z-index: 999;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  direction: rtl !important;

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 48px;
    height: 48px;

    &.active {
      .mob-bottom-nav__icon {
        background: rgba(61, 127, 255, 0.24); // #3D7FFF with 24% opacity
      }
    }

    &:not(.active):active {
      .mob-bottom-nav__icon {
        background: rgba(61, 127, 255, 0.12);
      }
    }
  }

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 50%; // עגול במקום מרובע
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }

  // Active state - selected icon gets blue color
  &__item.active &__icon {
    .icon{
      fill: #3C73DE;
      color: #3C73DE;
    }
  }
}
</style>
